import { useEffect } from 'react';

// Drop-in replacement for react-helmet-async, so the project needs no extra
// package. Accepts the same <title>, <meta> and <link> children and applies
// them to document.head, removing anything it created on unmount.
//
// On a client-rendered site this behaves the same as react-helmet-async did:
// both set the tags after React mounts. If you later move to server rendering,
// swap this back for the real package.

function applyHead(children) {
  const nodes = (Array.isArray(children) ? children : [children]).flat(Infinity);
  const created = [];

  nodes.forEach((node) => {
    if (!node || typeof node !== 'object' || !node.type || !node.props) return;
    const { type, props } = node;

    if (type === 'title') {
      const text = Array.isArray(props.children)
        ? props.children.join('')
        : String(props.children ?? '');
      if (text) document.title = text;
      return;
    }

    if (type !== 'meta' && type !== 'link') return;

    let selector = null;
    if (props.name) selector = `${type}[name="${props.name}"]`;
    else if (props.property) selector = `${type}[property="${props.property}"]`;
    else if (props.rel) selector = `${type}[rel="${props.rel}"]`;
    if (!selector) return;

    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement(type);
      document.head.appendChild(el);
      created.push(el);
    }
    Object.entries(props).forEach(([key, value]) => {
      if (key !== 'children' && typeof value === 'string') el.setAttribute(key, value);
    });
  });

  return created;
}

export function Helmet({ children }) {
  useEffect(() => {
    const created = applyHead(children);
    return () => created.forEach((el) => el.remove());
  });
  return null;
}

export default Helmet;